"use client"
import { useLanguage } from "@/app/(marketing)/components/lang-context"
import { useAuth } from "@/context/auth-context"
import { Flex } from "@/shared/components/flex"
import { NumberFlow } from "@/shared/components/number-flow"
import { ZentjesLogo } from "@/shared/components/zentjes-logo"
import { APP_VERSION } from "@zentjes/config"
import { motion, useReducedMotion } from "framer-motion"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useEffect } from "react"

type TransactionEl = { type: "transaction"; text: string; amount: number; hi: boolean }
type SparklineEl = { type: "sparkline"; label: string; trend: string; up: boolean; points: number[] }
type BudgetEl = { type: "budget"; category: string; percentage: number }
type StatEl = { type: "stat"; label: string; value: string }
type IconEl = { type: "icon"; icon: string }

type FloatingEl = (TransactionEl | SparklineEl | BudgetEl | StatEl | IconEl) & {
	x: string;
	y: string;
	layer: "fg" | "bg";
	floatSpeed: number;
}

type Props = Record<string, never>

/**
 * LandingView
 *
 * Landing screen view component.
 * Redirects authenticated sessions to `/dashboard`.
 */
export function LandingView(_: Props): React.JSX.Element | null {
	const router = useRouter()
	const { setShowAuthModal, sessionType, isPending } = useAuth()
	const { t } = useLanguage()

	useEffect(() => {
		if (!isPending && sessionType === "authenticated_user") {
			router.push("/dashboard")
		}
	}, [sessionType, isPending, router])

	function onStart(): void {
		setShowAuthModal(true)
	}

	function onSignIn(): void {
		setShowAuthModal(true)
	}

	if (isPending) return null
	if (sessionType === "authenticated_user") return null

	return (
		<div className="relative h-[100svh] max-h-[100svh] overflow-x-hidden overflow-y-auto bg-[#FAFAFA] dark:bg-[#050505] selection:bg-black/10 dark:selection:bg-white/20 text-slate-800 dark:text-white">
			<AmbientBackground />
			<FloatingElements />

			<div className="pointer-events-none absolute top-4 right-4 z-20 flex justify-center px-4">
				<VersionBadge />
			</div>

			<div className="relative z-10 mx-auto grid h-full w-full max-w-4xl grid-rows-[1fr_auto_auto] px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))] md:px-6 md:pb-8 md:pt-6">
				<main className="flex min-h-0 flex-col items-center justify-center text-center">
					<motion.div
						initial={{ opacity: 0, y: 24 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
						className="relative mb-[clamp(1rem,4vh,2.75rem)]"
					>
						<ZentjesLogo size="lg" id="landing-logo" />
					</motion.div>

					<div className="max-w-2xl text-center">
						<style jsx>{`
							@keyframes fade-up {
								from {
									opacity: 0;
									transform: translate3d(0, 20px, 0);
								}
								to {
									opacity: 1;
									transform: translate3d(0, 0, 0);
								}
							}
							.animate-fade-up {
								animation: fade-up 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
								will-change: opacity, transform;
							}
							.delay-150 { animation-delay: 150ms; }
							.delay-250 { animation-delay: 250ms; }
							.delay-350 { animation-delay: 350ms; }
						`}</style>
						<div className="space-y-0">
							<h1 className="opacity-0 text-[clamp(2.25rem,7vw,4.5rem)] font-serif leading-[1.1] tracking-[-0.02em] bg-gradient-to-br from-slate-500 via-slate-600 to-slate-400 dark:from-zinc-400 dark:via-zinc-300 dark:to-zinc-500 bg-clip-text text-transparent animate-fade-up delay-150">
								{t("landing.hero.title1")}
							</h1>
							<h1 className="opacity-0 text-[clamp(2.25rem,7vw,4.5rem)] font-serif italic leading-[1.1] tracking-[-0.02em] bg-gradient-to-br from-slate-800 via-slate-700 to-slate-500 dark:from-white dark:via-zinc-200 dark:to-zinc-400 bg-clip-text text-transparent animate-fade-up delay-250">
								{t("landing.hero.title2")}
							</h1>
						</div>

						<p className="opacity-0 mx-auto mt-[clamp(0.75rem,2vh,1.5rem)] max-w-sm text-sm text-slate-500 leading-relaxed dark:text-zinc-400 md:text-base animate-fade-up delay-350">
							{t("landing.hero.subtitle")}
						</p>
					</div>

					<motion.div
						initial={{ opacity: 0, y: 16 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.8 }}
						className="mt-[clamp(1rem,4vh,3rem)]"
					>
						<Flex dir="col" align="center" gap={20}>
							<button
								onClick={onStart}
								className="group relative px-8 py-3.5 md:py-3 bg-slate-900 dark:bg-white text-white dark:text-black text-sm font-medium rounded-full overflow-hidden transition-transform hover:scale-105 active:scale-95 min-h-[44px]"
							>
								<span className="relative z-10">{t("landing.cta.start")}</span>
								<div className="absolute inset-0 bg-slate-800 dark:bg-zinc-200 opacity-0 group-hover:opacity-100 transition-opacity" />
							</button>

							<button
								onClick={onSignIn}
								className="text-[12px] text-slate-500 dark:text-zinc-500 hover:text-slate-800 dark:hover:text-white transition-colors duration-300"
							>
								{t("landing.cta.account")}{" "}
								<span className="underline underline-offset-2 decoration-slate-300 dark:decoration-zinc-700 hover:decoration-slate-600 dark:hover:decoration-white transition-all">
									{t("landing.cta.signin")}
								</span>
							</button>
						</Flex>
					</motion.div>
				</main>

				<motion.footer
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ delay: 1.1, duration: 0.8 }}
					className="pb-1 pt-5"
				>
					<div className="max-w-md mx-auto">
						<p className="text-[10px] text-slate-500 dark:text-zinc-600 text-center leading-relaxed mb-4">
							{t("landing.footer.agreement")}{" "}
							<Link
								href="/terms"
								className="underline underline-offset-2 decoration-slate-300 dark:decoration-zinc-800 hover:decoration-slate-500 dark:hover:decoration-zinc-400 text-slate-600 dark:text-zinc-500 hover:text-slate-800 dark:hover:text-zinc-300 transition-all duration-300"
							>
								{t("landing.footer.terms")}
							</Link>{" "}
							{t("landing.footer.and")}{" "}
							<Link
								href="/privacy"
								className="underline underline-offset-2 decoration-slate-300 dark:decoration-zinc-800 hover:decoration-slate-500 dark:hover:decoration-zinc-400 text-slate-600 dark:text-zinc-500 hover:text-slate-800 dark:hover:text-zinc-300 transition-all duration-300"
							>
								{t("landing.footer.privacy")}
							</Link>
						</p>

						<Flex just="center" className="gap-6 text-[10px] uppercase tracking-[0.15em] text-slate-500 dark:text-zinc-600">
							<Link
								href="/developers"
								className="hover:text-slate-700 dark:hover:text-zinc-400 transition-colors duration-300 border-b border-transparent hover:border-slate-400/50 dark:hover:border-zinc-400/50 pb-0.5"
							>
								{t("landing.menu.developers")}
							</Link>
							<Link
								href="/changelog"
								className="hover:text-slate-700 dark:hover:text-zinc-400 transition-colors duration-300 border-b border-transparent hover:border-slate-400/50 dark:hover:border-zinc-400/50 pb-0.5"
							>
								{t("landing.menu.changelog")}
							</Link>
						</Flex>
					</div>
				</motion.footer>
			</div>
		</div>
	)
}

function VersionBadge() {
	return (
		<div className="rounded-full bg-gray-100 dark:bg-zinc-900/50 border border-gray-200 dark:border-zinc-800/50 backdrop-blur-md flex items-center px-2 py-1">
			<span className="text-[10px] font-mono text-gray-500 dark:text-zinc-400">v{APP_VERSION}</span>
		</div>
	)
}

function MiniSparkline({ points, up }: { points: number[]; up: boolean }) {
	const max = Math.max(...points)
	const min = Math.min(...points)
	const range = max - min || 1
	const h = 20
	const w = 48
	const step = w / (points.length - 1)
	const d = points
		.map((p, i) => `${i === 0 ? "M" : "L"}${i * step},${h - ((p - min) / range) * h}`)
		.join(" ")

	return (
		<svg width={w} height={h} className="overflow-visible">
			<motion.path
				d={d}
				fill="none"
				stroke={up ? "currentColor" : "currentColor"}
				strokeWidth={1.5}
				strokeLinecap="round"
				strokeLinejoin="round"
				className={up ? "text-emerald-500 dark:text-emerald-400" : "text-rose-400 dark:text-rose-400"}
				initial={{ pathLength: 0 }}
				animate={{ pathLength: 1 }}
				transition={{ duration: 1.5, delay: 1.5, ease: [0.25, 0.46, 0.45, 0.94] }}
			/>
		</svg>
	)
}

function FloatingCard({ el, delay }: { el: FloatingEl; delay: number }) {
	if (el.type === "transaction") {
		return (
			<div
				className={[
					"flex items-center gap-2 px-3 py-1.5 rounded-full border backdrop-blur-sm",
					el.hi
						? "bg-emerald-50/90 dark:bg-zinc-900/30 border-emerald-300 dark:border-zinc-800/40 text-emerald-800 dark:text-zinc-300 shadow-md shadow-emerald-100 dark:shadow-none"
						: "bg-white/90 dark:bg-zinc-950/20 border-slate-200 dark:border-white/[0.03] text-slate-700 dark:text-zinc-600 shadow-md shadow-slate-100 dark:shadow-none",
				].join(" ")}
			>
				<span className="text-[9px] uppercase tracking-wider font-semibold">{el.text}</span>
				<NumberFlow
					value={el.amount}
					format={{ style: "currency", currency: "USD", signDisplay: "always", trailingZeroDisplay: "stripIfInteger" }}
					className={["text-[11px] font-medium", el.hi ? "text-emerald-600 dark:text-zinc-200" : "text-slate-600 dark:text-zinc-500"].join(" ")}
				/>
			</div>
		)
	}

	if (el.type === "sparkline") {
		return (
			<div className="flex items-center gap-2.5 px-3 py-2 rounded-lg border backdrop-blur-sm bg-white/90 dark:bg-zinc-950/20 border-slate-200 dark:border-white/[0.03] shadow-md shadow-slate-100 dark:shadow-none">
				<div className="flex flex-col gap-0.5">
					<span className="text-[8px] uppercase tracking-wider text-slate-400 dark:text-zinc-500 font-medium">{el.label}</span>
					<span className={["text-[10px] font-semibold", el.up ? "text-emerald-600 dark:text-emerald-400" : "text-rose-500 dark:text-rose-400"].join(" ")}>
						{el.up ? "↗" : "↘"} {el.trend}
					</span>
				</div>
				<MiniSparkline points={el.points} up={el.up} />
			</div>
		)
	}

	if (el.type === "budget") {
		return (
			<div className="flex flex-col gap-1.5 px-3 py-2 rounded-lg border backdrop-blur-sm bg-white/90 dark:bg-zinc-950/20 border-slate-200 dark:border-white/[0.03] shadow-md shadow-slate-100 dark:shadow-none min-w-[90px]">
				<div className="flex items-center justify-between">
					<span className="text-[8px] uppercase tracking-wider text-slate-400 dark:text-zinc-500 font-medium">{el.category}</span>
					<span className="text-[9px] font-semibold text-slate-600 dark:text-zinc-400">{el.percentage}%</span>
				</div>
				<div className="h-1 rounded-full bg-slate-100 dark:bg-zinc-800 overflow-hidden">
					<motion.div
						className={[
							"h-full rounded-full",
							el.percentage > 85 ? "bg-rose-400" : el.percentage > 60 ? "bg-amber-400" : "bg-emerald-400",
						].join(" ")}
						initial={{ width: 0 }}
						animate={{ width: `${el.percentage}%` }}
						transition={{ duration: 1.2, delay: delay + 1.8, ease: [0.25, 0.46, 0.45, 0.94] }}
					/>
				</div>
			</div>
		)
	}

	if (el.type === "stat") {
		return (
			<div className="flex flex-col items-center gap-0.5 px-3 py-2 rounded-lg border backdrop-blur-sm bg-white/90 dark:bg-zinc-950/20 border-slate-200 dark:border-white/[0.03] shadow-md shadow-slate-100 dark:shadow-none">
				<span className="text-[8px] uppercase tracking-wider text-slate-400 dark:text-zinc-500 font-medium">{el.label}</span>
				<span className="text-[12px] font-semibold text-slate-700 dark:text-zinc-300">{el.value}</span>
			</div>
		)
	}

	if (el.type === "icon") {
		return (
			<div className="w-8 h-8 rounded-full border backdrop-blur-sm bg-white/80 dark:bg-zinc-950/30 border-slate-200 dark:border-white/[0.04] shadow-sm shadow-slate-100 dark:shadow-none flex items-center justify-center text-[14px]">
				{el.icon}
			</div>
		)
	}

	return null
}

function FloatingElements(): React.JSX.Element {
	const elements = floatingData()
	const prefersReducedMotion = useReducedMotion()

	return (
		<div className="absolute inset-0 overflow-hidden pointer-events-none hidden lg:block">
			<style jsx>{`
				@keyframes float-slow {
					0%, 100% { transform: translateY(0) translateZ(0); }
					50% { transform: translateY(-12px) translateZ(0); }
				}
				@keyframes float-medium {
					0%, 100% { transform: translateY(0) translateZ(0); }
					50% { transform: translateY(-8px) translateZ(0); }
				}
			`}</style>
			{elements.map((el, i) => {
				const fadeDelay = 0.1 + ((i * 13) % 17) * 0.08
				const isFg = el.layer === "fg"
				const opacity = isFg ? 1 : 0.6
				const scale = isFg ? 1 : 0.9
				const animationName = isFg ? "float-slow" : "float-medium"
				const animationDuration = el.floatSpeed

				return (
					<motion.div
						key={i}
						initial={{ opacity: 0, scale: scale * 0.85, y: 12 }}
						animate={{ opacity, scale }}
						transition={{
							opacity: { duration: 1.2, delay: fadeDelay },
							scale: { duration: 1.2, delay: fadeDelay },
						}}
						className="absolute will-change-transform"
						style={{
							left: el.x,
							top: el.y,
							...(prefersReducedMotion ? {} : {
								animation: `${animationName} ${animationDuration}s ease-in-out infinite`,
								animationDelay: `${fadeDelay + (i % 3) * 0.5}s`,
							}),
						}}
					>
						<FloatingCard el={el} delay={fadeDelay} />
					</motion.div>
				)
			})}
		</div>
	)
}

function floatingData(): FloatingEl[] {
	return [
		// Foreground layer (sharp, full opacity)
		{ type: "transaction", text: "Salary", amount: 3200, hi: true, x: "82%", y: "10%", layer: "fg", floatSpeed: 5 },
		{ type: "transaction", text: "Rent", amount: -1200, hi: false, x: "8%", y: "76%", layer: "fg", floatSpeed: 6 },
		{ type: "transaction", text: "Freelance", amount: 850, hi: true, x: "88%", y: "52%", layer: "fg", floatSpeed: 5.5 },
		{ type: "transaction", text: "Dining", amount: -85, hi: false, x: "5%", y: "55%", layer: "fg", floatSpeed: 5 },
		{ type: "sparkline", label: "Spending", trend: "12%", up: false, points: [8, 6, 9, 4, 7, 3, 5], x: "3%", y: "28%", layer: "fg", floatSpeed: 6 },
		{ type: "sparkline", label: "Income", trend: "8%", up: true, points: [3, 4, 3, 6, 5, 7, 9], x: "86%", y: "35%", layer: "fg", floatSpeed: 5.5 },
		{ type: "budget", category: "Shopping", percentage: 91, x: "20%", y: "22%", layer: "fg", floatSpeed: 5 },
		{ type: "budget", category: "Food", percentage: 72, x: "75%", y: "78%", layer: "fg", floatSpeed: 6 },
		{ type: "stat", label: "This month", value: "+$1,240", x: "4%", y: "16%", layer: "fg", floatSpeed: 5.5 },
		{ type: "stat", label: "Streak", value: "14 days", x: "90%", y: "68%", layer: "fg", floatSpeed: 5 },

		// Background layer (subtle, no blur)
		{ type: "transaction", text: "OF subscription", amount: -15.99, hi: false, x: "14%", y: "8%", layer: "bg", floatSpeed: 7 },
		{ type: "transaction", text: "Uber", amount: -24.50, hi: false, x: "92%", y: "20%", layer: "bg", floatSpeed: 8 },
		{ type: "transaction", text: "Invest", amount: 500, hi: true, x: "68%", y: "12%", layer: "bg", floatSpeed: 7.5 },
		{ type: "sparkline", label: "Savings", trend: "24%", up: true, points: [2, 3, 4, 3, 5, 6, 8], x: "10%", y: "65%", layer: "bg", floatSpeed: 8 },
		{ type: "budget", category: "Transport", percentage: 45, x: "85%", y: "88%", layer: "bg", floatSpeed: 7 },
		{ type: "icon", icon: "☀️", x: "35%", y: "8%", layer: "bg", floatSpeed: 7.5 },
		{ type: "icon", icon: "🏠", x: "18%", y: "88%", layer: "bg", floatSpeed: 8 },
		{ type: "icon", icon: "💰", x: "72%", y: "85%", layer: "bg", floatSpeed: 7 },
		{ type: "icon", icon: "📊", x: "28%", y: "90%", layer: "bg", floatSpeed: 8 },
	]
}

function AmbientBackground(): React.JSX.Element {
	return (
		<div className="absolute inset-0 overflow-hidden">
			<div
				className="dark:hidden absolute w-[140%] h-[140%] -left-[20%] -top-[20%]"
				style={{
					background: "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(255, 255, 255, 0.8) 0%, transparent 70%)"
				}}
			/>
			<div
				className="hidden dark:block absolute w-[140%] h-[140%] -left-[20%] -top-[20%]"
				style={{
					background: "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(15, 15, 15, 1) 0%, transparent 70%)"
				}}
			/>

			<div
				className="dark:hidden absolute w-[600px] h-[600px] rounded-full blur-[100px] opacity-[0.12]"
				style={{
					background: "linear-gradient(135deg, #93c5fd 0%, #c4b5fd 50%, #f9a8d4 100%)",
					left: "50%",
					top: "50%",
					transform: "translate(-50%, -50%)",
				}}
			/>
			<div
				className="hidden dark:block absolute w-[600px] h-[600px] rounded-full blur-[100px] opacity-[0.03]"
				style={{
					background: "linear-gradient(135deg, #ffffff 0%, #a0a0a0 50%, #404040 100%)",
					left: "50%",
					top: "50%",
					transform: "translate(-50%, -50%)",
				}}
			/>

			<div
				className="dark:hidden absolute inset-0 opacity-30"
				style={{
					backgroundImage: `url("data:image/svg+xml,%3Csvg width='200' height='200' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='dots' width='40' height='40' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='20' cy='20' r='1' fill='rgba(0,0,0,0.08)'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23dots)'/%3E%3C/svg%3E")`
				}}
			/>
			<div
				className="hidden dark:block absolute inset-0 opacity-30"
				style={{
					backgroundImage: `url("data:image/svg+xml,%3Csvg width='200' height='200' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='dots' width='40' height='40' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='20' cy='20' r='1' fill='rgba(255,255,255,0.03)'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23dots)'/%3E%3C/svg%3E")`
				}}
			/>
		</div>
	)
}
